/**
 * De erro de rede a erro de TELA — o unico tradutor do app.
 *
 * ## O defeito que este arquivo existe para tornar impossivel
 *
 * Um `422 VALIDATION_ERROR` renderizado como **lista vazia** e a pior forma de errar aqui: a
 * pessoa le "Nada encontrado" e conclui que a farmacia nao tem o remedio; quem depura le a
 * mesma tela e conclui que o catalogo esta incompleto. Ninguem ve o defeito, que e de contrato.
 * A regra passa a ser mecanica:
 *
 * | Situacao                                  | O que a tela mostra |
 * | ----------------------------------------- | ------------------- |
 * | a requisicao falhou (qualquer motivo)      | **erro**, com o texto do catalogo e o codigo |
 * | a requisicao nao foi feita (falta filial)  | o estado que diz **o que falta** e como resolver |
 * | a requisicao voltou com zero resultado     | "nada encontrado" — e so aqui |
 *
 * ## Por que aqui, e nao no `@farmacia/api-client`
 *
 * Porque o cliente HTTP **ja esta certo**: ele lanca `ApiError` com o codigo do catalogo
 * fechado (`API §2.1`) e nunca devolve colecao vazia no lugar de uma falha — nao ha o que
 * consertar la. O que estava errado era o consumo: telas que so olhavam `data`, ou que olhavam
 * `isError` e escreviam "sua conexao pode estar instavel" por cima de um erro de validacao.
 * Colapsar erro em vazio e decisao de **renderizacao**, e e onde a correcao pertence.
 *
 * ## Nenhuma frase em portugues nasce aqui
 *
 * Mesma regra do `packages/api-client/src/errors.ts`: *"a tela escolhe como mostrar, nunca
 * inventa o texto"*. O que este modulo devolve vem de `ERROR_CATALOG` (via `userMessage`) ou do
 * `detail` que o servidor mandou. O unico texto local e o de **sem rede**, que nao e estado da
 * API e por isso nao tem linha no catalogo (a DUVIDA ja esta registrada no cliente HTTP).
 *
 * **Nunca** `error.message`: para um `ApiError` ele e `"VALIDATION_ERROR (422) em GET
 * /v1/search"` — string de log. DESIGN_SYSTEM §7.11: *"Nunca exibir stack trace, 'Error 500'
 * nem o nome do gateway"*.
 */

import { isApiClientError, ApiError, ApiTransportError } from '@farmacia/api-client';
import { ERROR_CATALOG, isErrorCode, type ClientAction } from '@farmacia/contracts';

import { texto } from '../conteudo/index.js';

export interface ErroApresentavel {
  /** Uma frase em pt-BR, vinda do catalogo de erros ou do `detail` do servidor. */
  readonly mensagem: string;
  /**
   * O codigo do catalogo (`API §2.2`), para o `code` do `<ErrorState />`.
   *
   * `undefined` quando nao houve resposta conforme — inventar um codigo para "a rede caiu"
   * seria abrir pelo lado do cliente um catalogo que o `API §10.3` fecha.
   */
  readonly codigo: string | undefined;
  /**
   * Foi falta de rede? So neste caso a copy "sua conexao pode estar instavel" e verdade.
   * Em todo o resto ela e um palpite que desvia a investigacao.
   */
  readonly semRede: boolean;
  /**
   * A acao que o **catalogo** sugere para este codigo (`API §2.2`).
   *
   * `undefined` quando nao houve resposta conforme — nao ha catalogo para consultar.
   *
   * A tela usa isto para decidir **onde** desenhar o erro, e nao o que escrever. A distincao
   * que importa: `RETRY` e `REFRESH` sao falhas que repetir resolve, e um estado de pagina
   * inteira com "Tentar de novo" e a forma certa. `BLOCK` e o contrario — o servidor esta
   * dizendo *"assim nao da"*, e a saida e mudar alguma coisa nos controles da propria tela.
   * Cobrir a tela nesses casos remove justamente os controles que resolveriam.
   *
   * Foi assim que o checkout mostrava "Nao conseguimos conferir os valores" em tela cheia para
   * `OUT_OF_DELIVERY_AREA` e `MIN_ORDER_NOT_MET`, escondendo a agenda de enderecos, os radios
   * de modalidade e a saida de retirada — e oferecendo "Tentar de novo", que recota igual.
   */
  readonly acao: ClientAction | undefined;
}

/**
 * Traduz o que o TanStack Query chama de `error` (que e `unknown`) no que a tela mostra.
 *
 * Devolve `null` para **ausencia de erro**. O parametro e `unknown` de proposito: as telas
 * recebiam `erro: boolean` e continuam podendo passar `false`/`true` — `false` e "sem erro",
 * `true` e "houve um erro que ninguem soube descrever", que degrada para o texto generico.
 */
export function descreverErro(erro: unknown): ErroApresentavel | null {
  if (erro === null || erro === undefined || erro === false) return null;

  if (erro instanceof ApiTransportError) {
    if (erro.failure === 'OFFLINE') return SEM_REDE();
    return {
      mensagem: erro.userMessage ?? texto('erro.semConexao.descricao'),
      codigo: erro.fallbackCode,
      semRede: false,
      acao: undefined,
    };
  }

  if (erro instanceof ApiError) {
    return {
      acao: isErrorCode(erro.code) ? ERROR_CATALOG[erro.code].acaoSugerida : undefined,
      // `userMessage` e `detail ?? catalogMessage`. `null` acontece nas linhas em que o
      // catalogo escreve "—" (erro de integracao, sem usuario do outro lado); ali a tela ainda
      // precisa dizer alguma coisa, e o codigo ao lado e o que o atendente vai pedir.
      mensagem: erro.userMessage ?? texto('erro.semConexao.descricao'),
      codigo: erro.code,
      semRede: false,
    };
  }

  // `isApiClientError` cobre subclasses futuras da raiz comum sem que este arquivo precise
  // saber os nomes delas.
  if (isApiClientError(erro)) {
    return {
      mensagem: erro.userMessage ?? texto('erro.semConexao.descricao'),
      codigo: undefined,
      semRede: false,
      acao: undefined,
    };
  }

  // `erro === true`, um `Error` qualquer, uma string: nao da para afirmar nada sobre a causa.
  // O texto generico e honesto; o que nao pode acontecer e a tela seguir como se nada tivesse
  // falhado.
  return SEM_REDE();
}

function SEM_REDE(): ErroApresentavel {
  return {
    mensagem: texto('erro.semConexao.descricao'),
    codigo: undefined,
    semRede: true,
    acao: undefined,
  };
}

/** Houve erro? Atalho para o `if` das telas, sem repetir a comparacao com `null`. */
export function temErro(erro: unknown): boolean {
  return descreverErro(erro) !== null;
}
